"use client";

import { useEffect, useId, type CSSProperties, type ReactNode } from "react";

type ModalSize = "sm" | "md" | "lg";

interface ModalProps {
  visible: boolean;
  onClose: () => void;
  title?: string;
  ariaLabel?: string;
  children: ReactNode;
  size?: ModalSize;
}

const MAX_WIDTH_MAP: Record<ModalSize, number> = {
  sm: 320,
  md: 480,
  lg: 640,
};

const overlayStyle: CSSProperties = {
  position: "fixed",
  inset: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: "rgba(0,0,0,0.6)",
  zIndex: 9999,
  padding: 16,
};

const closeButtonStyle: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: 32,
  height: 32,
  borderRadius: "50%",
  border: "none",
  background: "var(--ish-surface-subtle, #f5f0e8)",
  color: "var(--ish-text-muted, #8a7968)",
  fontSize: 18,
  cursor: "pointer",
  flexShrink: 0,
  lineHeight: 1,
};

export default function Modal({
  visible,
  onClose,
  title,
  ariaLabel,
  children,
  size = "md",
}: ModalProps) {
  const titleId = useId();

  // Close on Escape key
  useEffect(() => {
    if (!visible) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [visible, onClose]);

  if (!visible) return null;

  const dialogStyle: CSSProperties = {
    background: "var(--ish-card, #fff)",
    borderRadius: 16,
    padding: 24,
    width: "100%",
    maxWidth: MAX_WIDTH_MAP[size],
    maxHeight: "90vh",
    display: "flex",
    flexDirection: "column",
    boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
  };

  return (
    <div style={overlayStyle} onClick={onClose} aria-hidden={!visible}>
      {/* Stop propagation so clicks inside don't dismiss */}
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={title != null ? titleId : undefined}
        aria-label={title == null ? ariaLabel || "Dialog" : undefined}
        style={dialogStyle}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 16,
            gap: 12,
          }}
        >
          {title != null ? (
            <h2
              id={titleId}
              style={{
                margin: 0,
                fontSize: 18,
                fontWeight: 700,
                color: "var(--ish-dark, #1a1311)",
                flex: 1,
              }}
            >
              {title}
            </h2>
          ) : (
            <div style={{ flex: 1 }} />
          )}

          <button type="button" aria-label="Close" onClick={onClose} style={closeButtonStyle}>
            {"\u00d7"}
          </button>
        </div>

        {/* Scrollable content */}
        <div style={{ overflowY: "auto", flex: 1 }}>{children}</div>
      </div>
    </div>
  );
}
